
import LoaderTriangle from "components/loader-triangle";
import Image from "next/image";
import { useEffect, useState } from "react";
import { useQuery } from '@tanstack/react-query';
import { MinWidth } from "../../../enums/screen-size.enum";
import useMediaQuery from "../../../hooks/useMediaQuery.hook";
import useTranslation from "../../../hooks/useTranslate.hook";
import {
    getDocumentUrl,
    getFilePageCount,
    getDownloadDocumentAllUrl,
    getExternalDocumentUrl,
    getExternalDownloadDocumentAllUrl,
    getExternalDownloadDocumentUrl
} from "../../../services/api";
import Button from "../../buttons/basic";
import Modal from "../../modal";
import SvgCloudDownload from "../../svgs/cloud-download";
import SvgMissingDocument from "../../svgs/missing-document";
import SvgNavigateBefore from "../../svgs/navigate-before";
import SvgNavigateNext from "../../svgs/navigate-next";
import SvgPrint from "components/svgs/print";
import DocumentDownload from "./document-download";

import { QueryKeys } from "@/enums/query-keys.enum";
import TooltipV2 from "components/tooltip-v2";
import {downloadUrl} from "@/utils/general.util";

const showAsPdfExtensions = ['.doc', '.docx', '.pdf', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.ics'];
const shouldShowAsPdf = (extension?: string) => extension && showAsPdfExtensions.includes(extension.toLowerCase());


type DocumentPreviewType = {
    onClose: Function;
    isOpen: boolean;
    selectedDocument: any;
    documentsArray?: any;
    title?: any;
    isExternal?: boolean;
};

const DocumentPreview = ({ isOpen, onClose, selectedDocument, documentsArray, isExternal }: DocumentPreviewType) => {
    const { t } = useTranslation();

    const isMobile = !useMediaQuery(MinWidth.Tablet);
    const isDesktop = useMediaQuery(MinWidth.Laptop);
    const [documentPreview, setDocumentPreview] = useState("");
    const [isDownloadRequested, setIsDownloadRequested] = useState(false);
    const [currentDocument, setCurrentDocument] = useState(selectedDocument);
    const [hasPrior, setHasPrior] = useState(false);
    const [hasNext, setHasNext] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isDocumentLoading, setIsDocumentLoading] = useState(true);

    const { data: filePageCount, isError: isErrorFilePageCount } = useQuery({
        queryKey: [QueryKeys.FilePageCount, currentDocument?.id!],
        queryFn: () => getFilePageCount(currentDocument?.id),
        enabled: (currentDocument != null && !isDesktop), gcTime: 0, retry: 0
    });
    
    const changeDocument = (isNext = true) => {

        setIsDownloadRequested(false);

        let documentIndex = documentsArray.map((doc: any) => doc.id).indexOf(currentDocument.id);
        setIsDocumentLoading(true);

        if (isNext && documentIndex < documentsArray?.length) {
            let doc = documentsArray[documentIndex + 1];

            setCurrentDocument(doc);

            setHasPrior(true);
            if (documentIndex + 1 == documentsArray?.length - 1) {
                setHasNext(false);
            }
        } else if (!isNext && documentIndex > 0) {
            let doc = documentsArray[documentIndex - 1];

            setCurrentDocument(doc);

            setHasNext(true);
            if (documentIndex - 1 == 0) {
                setHasPrior(false);
            }
        }
    }

    const getConvertToPdf = () => {
        if (currentDocument.fileName.indexOf(".odt") >= 0) return "&convertToPng=true";

        return "&convertToPdf=true";
    }

    useEffect(() => {
        if (isDesktop) {
            setHasError(false);
            setDocumentPreview(isExternal ? getExternalDocumentUrl(currentDocument.id!, currentDocument.fileName) + getConvertToPdf() : getDocumentUrl(currentDocument.id!, currentDocument.fileName) + getConvertToPdf() );
        }
        else if (currentDocument) {
            setHasError(false);
            setDocumentPreview(isExternal ? getExternalDocumentUrl(currentDocument.id!, currentDocument.fileName) + getConvertToPdf() : getDocumentUrl(currentDocument.id!, currentDocument.fileName) + "&convertToPng=true");
        }
    }, [isDesktop, currentDocument]);

    useEffect(() => {
        if (!!documentsArray) {
            let documentIndex = documentsArray.map((doc: any) => doc.id).indexOf(currentDocument.id);
            if (documentIndex < documentsArray?.length - 1) {
                setHasNext(true);
            }
            if (documentIndex > 0) {
                setHasPrior(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isErrorFilePageCount) {
            setHasError(true);
            setIsDocumentLoading(false);
        }
    }, [isErrorFilePageCount]);
    
    return (
        <>
            <Modal
                isOpen={isOpen}
                closeModal={onClose}
                title={t("document_preview_key")}
                size="huge"
                classConfig="c-documents-preview-modal"
            >
                {/*embeded object is not supported on mobile so it will require imag implementation, on API => URL?convertToPng=true*/}
                <div className="c-documents-preview">
                    <div className="c-documents-preview-action-bar">
                        {documentsArray &&
                            <Button variant="outlined"
                                icon={isMobile ? <SvgNavigateBefore key="svg-navigate-previous" /> : undefined}
                                text={isMobile ? "" : t("previous_document")}
                                shape={isMobile ? "circle" : "round"}
                                color={!hasPrior ? "grey" : "success"}
                                disabled={!hasPrior}
                                onClick={() => { changeDocument(false) }} />
                        }
                        <TooltipV2 text={t("download_file")}>
                            <Button color="success" shape="circle" variant="outlined" icon={[<SvgCloudDownload key="svg-checkbox-outline" />]} onClick={() => setIsDownloadRequested(true)} ></Button>
                        </TooltipV2>
                        <TooltipV2 text={t("download_all_files")}>
                            <Button color="success" shape="circle" variant="outlined" icon={[<SvgPrint key="svg-print" />]} onClick={() => {
                                const url = isExternal ? getExternalDownloadDocumentAllUrl(documentsArray.map((item:any) => item.id)) : getDownloadDocumentAllUrl(documentsArray.map((item:any) => item.id));
                                downloadUrl(url, 'download.pdf');
                            }} ></Button>
                        </TooltipV2>
                        <div>{t("file_name")}: {currentDocument.fileName}</div>
                        {documentsArray &&
                            <Button variant="outlined"
                                icon={isMobile ? <SvgNavigateNext key="svg-navigate-next" /> : undefined}
                                text={isMobile ? "" : t("next_document")}
                                shape={isMobile ? "circle" : "round"}
                                color={!hasNext ? "grey" : "success"}
                                disabled={!hasNext}
                                onClick={changeDocument} />
                        }
                    </div>
                    {isDocumentLoading && !hasError &&
                        <div>
                            <LoaderTriangle loading={isDocumentLoading} text={t("document_loading")} />
                        </div>
                    }
                    {(isDesktop ?
                        (documentPreview && !hasError && (
                            <>
                                {
                                  shouldShowAsPdf(currentDocument?.extension)
                                    ? (
                                        <iframe
                                            src={documentPreview}
                                            width="100%"
                                            height="100%"
                                            id="document-preview"
                                            className={`c-documents-preview-embed ${isDocumentLoading && "c-documents-preview-hide"}`}
                                            onError={() => {
                                                setHasError(true);
                                                setIsDocumentLoading(false);
                                            }}
                                            onLoad={() => setIsDocumentLoading(false)}
                                        />
                                    ) : (
                                        <div className="c-documents-preview__image-wrap">
                                            <Image
                                                src={documentPreview}
                                                alt=""
                                                className="c-documents-preview-img-v2"
                                                onError={(e) => {
                                                    //setHasError(true);
                                                    setIsDocumentLoading(false);
                                                }}
                                                onLoad={() => setIsDocumentLoading(false)}
                                                width={1200}
                                                height={800}
                                            />
                                        </div>

                                    )}
                            </>
                        ))
                        :
                        // eslint-disable-next-line @next/next/no-img-element
                        (documentPreview && filePageCount != null && !hasError && <div className={"c-documents-preview-img-wrapper " + (filePageCount > 1 ? "c-documents-preview-img-wrapper-multipage" : "")}>
                            {
                                [...Array(filePageCount)].map((e, i) =>
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img
                                        key={i}
                                        src={documentPreview + "&pageCount=" + i}
                                        className="c-documents-preview-img"
                                        alt="cv preview url"
                                        onError={() => { setHasError(true); setIsDocumentLoading(false) }}
                                        onLoad={() => setIsDocumentLoading(false)} />)
                            }
                        </div>))
                    }
                    {/*{hasError &&*/}
                    {/*    <div className="c-documents-preview-error">*/}
                    {/*        <SvgMissingDocument></SvgMissingDocument>*/}
                    {/*        <div>{t("document_unavailable")}</div>*/}
                    {/*    </div>*/}
                    {/*}*/}
                </div>
            </Modal>
            {/* <iframe id="my_iframe" className="c-documents-preview-hidden-iframe" src={documentDownloadUrl}></iframe> */}
            {isDownloadRequested && <DocumentDownload closeRequest={() => { setIsDownloadRequested(false) }} documentId={currentDocument.id!} customUrl={isExternal ? getExternalDownloadDocumentUrl(currentDocument.id) : undefined} />}
        </>
    )
};

export default DocumentPreview;
